import type React from 'react';
import { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useTranslate } from 'sbx-crm-menu-ui';

interface RolesComponentProps {
    value: string[];
    setValue: (value: string[]) => void;
    disabled?: boolean;
    title?: string;
    description?: string;
    label?: string;
    placeholder?: string;
}

export default function RolesComponent({ value, setValue, disabled, title, description, label, placeholder }: RolesComponentProps) {
    const [newOption, setNewOption] = useState('');
    const { t: agent } = useTranslate('agent');
    const { t: common } = useTranslate('common');

    const handleOptionRemove = (option: string) => {
        setValue(value.filter((item) => item !== option));
    };

    const handleAddCustomOption = () => {
        const trimmedOption = newOption.trim();
        if (trimmedOption && !value.includes(trimmedOption)) {
            setValue([...value, trimmedOption]);
            setNewOption('');
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleAddCustomOption();
        }
    };

    const clearAll = () => {
        setValue([]);
    };

    return (
        <div className="">
            <Card>
                <CardHeader>
                    <CardTitle>{title ?? agent('add_roles')}</CardTitle>
                    <CardDescription>{description ?? agent('roles_description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">

                    {/* Add Custom Option */}
                    <div>
                        <h3 className="text-sm font-medium mb-3">{label ?? agent('add_own_role')}:</h3>
                        <div className="flex gap-2">
                            <Input
                                placeholder={`${placeholder ?? agent('custom_role')}...`}
                                value={newOption}
                                onChange={(e) => setNewOption(e.target.value)}
                                onKeyPress={handleKeyPress}
                                className="flex-1"
                                disabled={disabled}
                            />
                            <Button
                                onClick={handleAddCustomOption}
                                disabled={!newOption.trim() || value.includes(newOption.trim()) || disabled}
                                size="sm"
                            >
                                <Plus className="h-4 w-4" />
                                {common('add')}
                            </Button>
                        </div>
                        {newOption.trim() && value.includes(newOption.trim()) && (
                            <p className="text-xs text-destructive mt-1">{common('option_already_added')}</p>
                        )}
                    </div>

                    {/* Selected Options */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-medium">
                            {agent('added_options')} ({value.length}):
                            </h3>
                            {value.length > 0 && (
                                <Button variant="ghost" size="sm" disabled={disabled} onClick={clearAll}>
                                    {common('clear_all')}
                                </Button>
                            )}
                        </div>

                        {value.length === 0 ? (
                            <p className="text-sm text-muted-foreground">{common('no_options_yet')}</p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {value.map((option) => (
                                    <Badge key={option} variant="default" className="flex items-center gap-1 py-[5px]">
                                        {option}
                                        <button
                                            disabled={disabled}
                                            onClick={() => handleOptionRemove(option)}
                                            className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Array Display */}
                    <div className="mt-4 p-3 bg-muted rounded-md">
                        <h4 className="text-sm font-medium mb-2">Current Array:</h4>
                        <code className="text-sm">{JSON.stringify(value, null, 2)}</code>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
